import { Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import Paper from '@mui/material/Paper'
import { ButtonDeleteSimple } from 'component/Button/ButtonDelete'
import InputNewRangeTimePicker from 'component/Input/InputNewRangeTimePicker'
import { colors } from 'constants/theme'
import { ValueLabel } from 'features/setting/personnel-managements/personnels/form-personnel-value'
import * as UseStyled from 'features/setting/personnel-managements/personnels/useStyles'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

/** UTILS || SLICE */
import { editSchedule, selectLoadingSchedule, selectSchedule } from 'app/slice/user.slice'
import TextInput from 'new-components/inputs/text-input'

interface ScheduleInterface {
  dayOfWork: string
  timeOfWork: string
  name: string
}
interface ScheduleProps {
  isEdit: boolean
}
export default function Schedule(props: ScheduleProps) {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()
  const loading = useSelector(selectLoadingSchedule)
  const userSchedule = useSelector(selectSchedule)

  const [schedule, setSchedule] = useState<ScheduleInterface[]>([])
  const dateOfWeekTH = ['จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส', 'อา']
  const dateOfWeeks = [
    { day: t('PERSONNEL.SCHEDULE.NAME_DAY.MON') },
    { day: t('PERSONNEL.SCHEDULE.NAME_DAY.TUE') },
    { day: t('PERSONNEL.SCHEDULE.NAME_DAY.WED') },
    { day: t('PERSONNEL.SCHEDULE.NAME_DAY.THU') },
    { day: t('PERSONNEL.SCHEDULE.NAME_DAY.FRI') },
    { day: t('PERSONNEL.SCHEDULE.NAME_DAY.SAT') },
    { day: t('PERSONNEL.SCHEDULE.NAME_DAY.SUN') }
  ]
  const headTable = [t('PERSONNEL.SCHEDULE.DAY'), t('PERSONNEL.SCHEDULE.WORK_LOCATION'), t('PERSONNEL.SCHEDULE.TIME_WORK'), 'Action']

  useEffect(() => {
    if (!loading) {
      if (props.isEdit) setSchedule(userSchedule.userWorkplaces)
    }
  }, [loading])

  const handleCheckDate = (date: string) => {
    if (i18n.language === 'en') {
      const indexItem = dateOfWeeks.findIndex((item) => item.day === date)
      return dateOfWeekTH[indexItem] || date
    } else {
      return date
    }
  }

  const handleValueSchedule = (dayArray: any, dateOfWeek: any, rowIndex: number) => {
    const newDate = handleCheckDate(dateOfWeek.day)
    const newData: any = [...schedule].map((object: any, stateIndex: any) => {
      const check = dayArray.includes(newDate)
      if (stateIndex === rowIndex) {
        if (!check) {
          return { ...object, dayOfWork: [...dayArray, newDate].toString() }
        } else {
          return { ...object, dayOfWork: dayArray.filter((day: any) => day !== newDate).toString() }
        }
      } else {
        return object
      }
    })
    const dataArray = newData[rowIndex]?.dayOfWork.split(',')
    const sortedDataArray = dataArray.sort((a: any, b: any) => {
      const order = 'จ,อ,พ,พฤ,ศ,ส,อา'.split(',')
      return order.indexOf(a) - order.indexOf(b)
    })
    const newDataSort = sortedDataArray.join(',')
    newData[rowIndex] = { ...newData[rowIndex], dayOfWork: newDataSort }

    dispatch(
      editSchedule({
        value: newData,
        key: 'userWorkplaces'
      })
    )
    setSchedule(newData)
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography sx={{ fontSize: 20, fontWeight: 600, ml: 1, mt: 2 }}>
        {t('PERSONNEL.WORK_SCHEDULE')}
      </Typography>
      <TableContainer
        component={Paper}
        sx={{ mt: 3, boxShadow: 'none', border: `1px solid ${colors.lightGray}` }}
      >
        <Table aria-label="work schedule table" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              {headTable.map((head, i) => (
                <TableCell key={i} sx={{ color: '#fff', bgcolor: '#2F4269' }}>
                  <Typography sx={{ fontWeight: 600, color: '#fff' }}>
                    {head}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell
                colSpan={5}
                onClick={() =>
                  setSchedule(prev => [
                    ...prev,
                    { dayOfWork: '', timeOfWork: '00:00/00:00', name: '' }
                  ])
                }
                sx={{
                  backgroundColor: 'transparent !important',
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: colors.extraLightBlue }
                }}
              >
                <UseStyled.DivAdd>
                  + {t('PERSONNEL.ADD_WORK_SCHEDULE')}
                </UseStyled.DivAdd>
              </TableCell>
            </TableRow>
            {schedule.map(({ dayOfWork, timeOfWork, name }, rowIndex) => (
              <TableRow key={rowIndex}>
                <TableCell sx={{ width: 300 }}>
                  {dateOfWeeks.map((dateOfWeek, index) => {
                    const dayArray = dayOfWork.split(',')
                    const isActive = dayArray.includes(handleCheckDate(dateOfWeek.day))
                    return (
                      <Button
                        key={index}
                        onClick={() => handleValueSchedule(dayArray, dateOfWeek, rowIndex)}
                        sx={{
                          p: 0,
                          minWidth: 0,
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          fontSize: '14px',
                          fontWeight: 500,
                          textTransform: 'none',
                          ml: index === 0 ? 0 : '5px',
                          bgcolor: isActive ? colors.highlightSelect : colors.lightGray,
                          color: isActive ? colors.themeSecondColor : colors.black,
                          '&:hover': {
                            bgcolor: isActive ? colors.highlightSelect : colors.lightGray,
                          },
                        }}
                      >
                        {dateOfWeek.day}
                      </Button>
                    )
                  })}
                </TableCell>
                <TableCell>
                  <TextInput
                    label={t('PERSONNEL.SCHEDULE.NAME_WORK')}
                    value={name}
                    onChange={e => {
                      const updated = schedule.map((item, i) =>
                        i === rowIndex ? { ...item, name: e.target.value } : item
                      );
                      setSchedule(updated);
                      dispatch(editSchedule({ value: updated, key: 'userWorkplaces' }));
                    }}
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <InputNewRangeTimePicker
                    value={[moment(schedule[rowIndex].timeOfWork.split('/')[0], 'HH:mm').format('HH:mm'), moment(schedule[rowIndex].timeOfWork.split('/')[1], 'HH:mm').format('HH:mm')]}
                    onChange={(time) => {
                      if (time[0] && time[1]) {
                        const val: any = [...schedule].map((object: any, stateIndex: any) => {
                          return stateIndex === rowIndex ? { ...object, timeOfWork: `${time[0]}/${time[1]}` } : object
                        })
                        setSchedule(val)
                        dispatch(editSchedule({ value: val, key: 'userWorkplaces' }))
                      } else {
                        const val: any = [...schedule].map((object: any, stateIndex: any) => (stateIndex === rowIndex ? { ...object, timeOfWork: '' } : object))
                        setSchedule(val)
                        dispatch(editSchedule({ value: val, key: 'userWorkplaces' }))
                      }
                      ValueLabel.userWorkplaces = schedule
                    }}
                    style={{ maxWidth: '300px' }}
                  />
                </TableCell>
                <TableCell
                  onClick={() => {
                    const filtered = schedule.filter((_, i) => i !== rowIndex);
                    setSchedule(filtered);
                    dispatch(editSchedule({ value: filtered, key: 'userWorkplaces' }));
                  }}
                  sx={{ cursor: 'pointer' }}
                >
                  <ButtonDeleteSimple />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
